import React, { useState, useEffect } from 'react';
import DateInput from '../../components/DateInput';
import api from '../../api/axios';
import { toast } from 'react-toastify';

// The containers (and their weights) are captured once, on the BL / Shipments
// tab. Material Receipt reuses that list instead of asking the user to retype
// it. The BL weight is shown as-is (MT) for reference; the receipt date, slip
// no and the mill / ICD weighments are filled in here, per container.
function blContainersOf(order) {
  const rows = [];
  for (const sh of order.shipments || []) {
    let list = sh.container_dates;
    if (typeof list === 'string') { try { list = JSON.parse(list); } catch { list = []; } }
    if (!Array.isArray(list)) continue;
    for (const c of list) {
      const no = (c && c.container_no || '').trim();
      if (!no) continue;
      const mt = parseFloat(c.weight);
      rows.push({
        container_no: no,
        seal_no: '',
        bl_weight_mt: isNaN(mt) ? '' : mt.toFixed(3),
        received_date: '',
        mill_weight_mt: '',
        icd_weight_mt: '',
        shipment_id: sh.id,
      });
    }
  }
  return rows;
}

const contKey = c => (c && c.container_no || '').trim().toUpperCase();

// Existing rows win; BL containers not yet on the receipt get appended.
function mergeBlContainers(existing, bl) {
  const byNo = new Map(bl.map(c => [contKey(c), c]));
  const seen = new Set((existing || []).map(contKey).filter(Boolean));
  const kept = (existing || []).map(c => ({ ...c, bl_weight_mt: byNo.get(contKey(c))?.bl_weight_mt ?? c.bl_weight_mt ?? '' }));
  return [...kept, ...bl.filter(c => !seen.has(contKey(c)))];
}

export default function ReceiptPanel({ order, onRefresh }) {
  const [form, setForm] = useState({ containers:[] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/receipts/order/${order.id}`).then(r => {
      const bl = blContainersOf(order);
      if (r.data) {
        setForm({ ...r.data, containers: mergeBlContainers(r.data.containers?.filter(Boolean) || [], bl) });
      } else {
        setForm(f => ({ ...f, containers: bl }));
      }
    });
  }, [order.id]);

  const updateContainer = (i, k, v) => setForm(f => { const c = [...f.containers]; c[i] = {...c[i],[k]:v}; return {...f,containers:c}; });

  const save = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post(`/receipts/order/${order.id}`, form);
      toast.success('Material receipt saved'); onRefresh();
      const r = await api.get(`/receipts/order/${order.id}`);
      // Re-read so the form shows exactly what was stored.
      if (r.data) setForm({ ...r.data, containers: mergeBlContainers(r.data.containers?.filter(Boolean) || [], blContainersOf(order)) });
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="card">
      <form onSubmit={save}>
        {(() => {
          // Column totals shown beside the heading.
          const sum = (key) => (form.containers||[]).reduce((t,c)=>t+(parseFloat(c[key])||0),0);
          const t3 = (n) => n.toFixed(3);
          return (
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:'12px',marginBottom:'8px'}}>
              <div className="section-title" style={{margin:0}}>Container Details</div>
              <div style={{display:'flex',gap:'18px',fontSize:'13px',fontWeight:600}}>
                <span>Container Wt: <span style={{color:'var(--primary,#1d4ed8)'}}>{t3(sum('bl_weight_mt'))} MT</span></span>
                <span>Mill Wt: <span style={{color:'var(--primary,#1d4ed8)'}}>{t3(sum('mill_weight_mt'))} MT</span></span>
                <span>ICD Wt: <span style={{color:'var(--primary,#1d4ed8)'}}>{t3(sum('icd_weight_mt'))} MT</span></span>
              </div>
            </div>
          );
        })()}
        {(form.containers||[]).map((c,i) => (
          <div key={i} className="card" style={{background:'#f8fafc',marginBottom:'10px',padding:'12px'}}>
            <div className="form-row-tight">
              <div className="form-group"><label>Container No</label><input className="form-control" value={c.container_no||''} onChange={e=>updateContainer(i,'container_no',e.target.value)} /></div>
              <div className="form-group"><label>Container Weight</label><input className="form-control" value={c.bl_weight_mt||''} readOnly /></div>
              <div className="form-group"><label>Receipt Date</label><DateInput className="form-control" value={c.received_date||''} onChange={e=>updateContainer(i,'received_date',e.target.value)} /></div>
              <div className="form-group"><label>Slip No</label><input className="form-control" value={c.seal_no||''} onChange={e=>updateContainer(i,'seal_no',e.target.value)} /></div>
              <div className="form-group"><label>Mill Weight</label><input className="form-control" type="number" step="0.001" value={c.mill_weight_mt||''} onChange={e=>updateContainer(i,'mill_weight_mt',e.target.value)} /></div>
              <div className="form-group"><label>ICD Weight</label><input className="form-control" type="number" step="0.001" value={c.icd_weight_mt||''} onChange={e=>updateContainer(i,'icd_weight_mt',e.target.value)} /></div>
              {order.shipments?.length > 1 && <div className="form-group"><label>BL Reference</label><select className="form-control" value={c.shipment_id||''} onChange={e=>updateContainer(i,'shipment_id',e.target.value)}><option value="">—</option>{order.shipments.map(s=><option key={s.id} value={s.id}>{s.bl_no}</option>)}</select></div>}
            </div>
          </div>
        ))}
        <div className="btn-group">
          <button className="btn btn-primary" type="submit" disabled={saving}>{saving?'Saving...':'Save Receipt'}</button>
        </div>
      </form>
    </div>
  );
}
